import { useState, useEffect } from 'react'
import toast from 'react-hot-toast'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { Calendar as CalendarIcon, Clock, AlertCircle, CheckCircle, Loader2, UserX, Video, MapPin, Users } from 'lucide-react'
import { format } from 'date-fns'
import { reviewService } from '@/services/api/reviewService'
import { useAuth } from '@/providers/AuthProvider'

interface ScheduleCollaborativeSessionDialogProps {
  open: boolean
  onClose: () => void
  employeeName: string
  employeeId: string
  managerId: string
  sessionType?: string
  isRescheduling?: boolean
  existingScheduledDate?: string | null
  calendarEventId?: string | null
  // Existing session details for pre-population when rescheduling
  existingDuration?: number
  existingMeetingType?: 'face-to-face' | 'virtual' | 'hybrid'
  existingLocation?: string
  existingMeetingLink?: string
  existingAgenda?: string
  onSchedule: (scheduleData: CollaborativeScheduleData) => void
}

export interface CollaborativeScheduleData {
  scheduledDate: Date
  scheduledTime: string
  durationMinutes: number
  meetingType: 'face-to-face' | 'virtual' | 'hybrid'
  location: string
  meetingLink?: string
  agenda: string
}

export default function ScheduleCollaborativeSessionDialog({
  open,
  onClose,
  employeeName,
  employeeId,
  managerId,
  sessionType = 'objective_setting',
  isRescheduling = false,
  existingScheduledDate,
  calendarEventId,
  existingDuration,
  existingMeetingType,
  existingLocation,
  existingMeetingLink,
  existingAgenda,
  onSchedule
}: ScheduleCollaborativeSessionDialogProps) {
  const { user } = useAuth()
  
  const [scheduledDate, setScheduledDate] = useState<Date | undefined>(() => {
    if (existingScheduledDate) {
      try {
        const dateString = existingScheduledDate.endsWith('Z') ? existingScheduledDate : existingScheduledDate + 'Z'
        const date = new Date(dateString)
        // Validate the date is valid
        if (!isNaN(date.getTime())) {
          return date
        }
      } catch {
        // Invalid date string
      }
    }
    return undefined
  })
  
  const [scheduledTime, setScheduledTime] = useState(() => {
    if (existingScheduledDate) {
      try {
        const dateString = existingScheduledDate.endsWith('Z') ? existingScheduledDate : existingScheduledDate + 'Z'
        const date = new Date(dateString)
        // Validate the date is valid
        if (!isNaN(date.getTime())) {
          const hours = date.getHours()
          const minutes = date.getMinutes()
          const roundedMinutes = minutes < 15 ? '00' : minutes < 45 ? '30' : '00'
          const roundedHours = minutes >= 45 ? (hours + 1) % 24 : hours
          return `${String(roundedHours).padStart(2, '0')}:${roundedMinutes}`
        }
      } catch {
        // Invalid date string
      }
    }
    return ''
  })
  
  const [durationMinutes, setDurationMinutes] = useState(existingDuration || 60) // Default 60 min for collab sessions
  const [meetingType, setMeetingType] = useState<'face-to-face' | 'virtual' | 'hybrid'>(existingMeetingType || 'virtual')
  const [location, setLocation] = useState(existingLocation || '')
  const [meetingLink, setMeetingLink] = useState(existingMeetingLink || '')
  const [agenda, setAgenda] = useState(existingAgenda || '')
  
  // Availability checking state
  const [isCheckingAvailability, setIsCheckingAvailability] = useState(false)
  const [availabilityStatus, setAvailabilityStatus] = useState<'available' | 'conflict' | 'unknown'>('unknown')
  const [conflicts, setConflicts] = useState<Array<{ email: string; employee_id?: string; employee_ids?: string[]; busy_items: any[] }>>([])

  // Update all fields when dialog opens - either for new scheduling or rescheduling
  useEffect(() => {
    if (!open) return
    
    if (import.meta.env.DEV) console.log('� Dialog opened:', {
      isRescheduling,
      employeeId,
      existingScheduledDate,
      existingDuration,
      existingMeetingType,
      existingLocation,
      existingMeetingLink,
      existingAgenda
    })
    
    // For rescheduling, pre-populate all fields from existing session
    if (isRescheduling && existingScheduledDate) {
      if (import.meta.env.DEV) console.log('🔄 Rescheduling - pre-populating fields')
      
      try {
        // Handle both ISO strings with timezone (e.g., +00:00) and without (add Z)
        // Don't add Z if there's already a timezone offset
        const hasTimezone = existingScheduledDate.includes('+') || existingScheduledDate.includes('Z')
        const dateString = hasTimezone ? existingScheduledDate : existingScheduledDate + 'Z'
        const date = new Date(dateString)
        
        if (import.meta.env.DEV) console.log('📆 Parsing date:', { original: existingScheduledDate, dateString, parsed: date })
        
        if (!isNaN(date.getTime())) {
          setScheduledDate(date)
          
          const hours = date.getHours()
          const minutes = date.getMinutes()
          const roundedMinutes = minutes < 15 ? '00' : minutes < 45 ? '30' : '00'
          const roundedHours = minutes >= 45 ? (hours + 1) % 24 : hours
          const timeStr = `${String(roundedHours).padStart(2, '0')}:${roundedMinutes}`
          setScheduledTime(timeStr)
          
          if (import.meta.env.DEV) console.log('✅ Date/time set:', { date, timeStr, hours, minutes })
        } else {
          if (import.meta.env.DEV) console.log('❌ Invalid date object')
        }
      } catch (err) {
        if (import.meta.env.DEV) console.log('❌ Date parse error:', err)
      }
      
      // Set other fields
      const duration = existingDuration || 60
      const type = existingMeetingType || 'virtual'
      const loc = existingLocation || ''
      const link = existingMeetingLink || ''
      const agendaText = existingAgenda || ''
      
      setDurationMinutes(duration)
      setMeetingType(type)
      setLocation(loc)
      setMeetingLink(link)
      setAgenda(agendaText)
      
      if (import.meta.env.DEV) console.log('✅ Other fields set:', { duration, type, loc, link, agendaText })
    } else {
      // For new scheduling, reset to defaults
      if (import.meta.env.DEV) console.log('🆕 New scheduling - resetting to defaults')
      setScheduledDate(undefined)
      setScheduledTime('')
      setDurationMinutes(60)
      setMeetingType('virtual')
      setLocation('')
      setMeetingLink('')
      setAgenda('')
    }
  }, [open, isRescheduling, employeeId, existingScheduledDate, existingDuration, existingMeetingType, existingLocation, existingMeetingLink, existingAgenda])
  
  // Check availability when date, time, or duration changes
  useEffect(() => {
    const checkAvailability = async () => {
      if (!scheduledDate || !scheduledTime || !user?.employeeId || !employeeId) {
        setAvailabilityStatus('unknown')
        return
      }
      
      try {
        setIsCheckingAvailability(true)
        
        // Combine date and time - validate date first
        const [hours, minutes] = scheduledTime.split(':').map(Number)
        if (isNaN(hours) || isNaN(minutes)) {
          setAvailabilityStatus('unknown')
          return
        }
        
        const dateTime = new Date(scheduledDate)
        // Validate the date is valid before proceeding
        if (isNaN(dateTime.getTime())) {
          setAvailabilityStatus('unknown')
          return
        }
        dateTime.setHours(hours, minutes, 0, 0)
        
        // Check availability for both manager and employee
        const result = await reviewService.checkAvailability(
          [user.employeeId, employeeId],
          dateTime,
          durationMinutes,
          calendarEventId
        )
        
        if (result.error) {
          console.warn('Availability check failed:', result.error)
          setAvailabilityStatus('unknown')
        } else if (result.available) {
          setAvailabilityStatus('available')
          setConflicts([])
        } else {
          setAvailabilityStatus('conflict')
          setConflicts(result.conflicts || [])
        }
      } catch (error) {
        console.error('Error checking availability:', error)
        setAvailabilityStatus('unknown')
      } finally {
        setIsCheckingAvailability(false)
      }
    }
    
    // Debounce the availability check
    const timeoutId = setTimeout(checkAvailability, 500)
    return () => clearTimeout(timeoutId)
  }, [scheduledDate, scheduledTime, durationMinutes, user?.employeeId, employeeId, calendarEventId])

  const handleSchedule = () => {
    if (!scheduledDate || !scheduledTime) {
      toast.error('Please select a date and time', { duration: 3000 })
      return
    }

    if (!location && meetingType !== 'virtual') {
      toast.error('Please specify a location', { duration: 3000 })
      return
    }

    onSchedule({
      scheduledDate,
      scheduledTime,
      durationMinutes,
      meetingType,
      location: meetingType === 'virtual' ? 'Virtual (MS Teams)' : location,
      meetingLink: meetingType !== 'face-to-face' ? meetingLink : undefined,
      agenda
    })
    onClose()
  }

  const getSessionTypeLabel = (type: string) => {
    const labels: { [key: string]: string } = {
      'objective_setting': 'Objective Setting Session',
      'objective_revision': 'Objective Revision Session',
      'performance_discussion': 'Impact & Growth Discussion',
      'career_planning': 'Career Planning Discussion'
    }
    return labels[type] || 'Collaborative Planning Session'
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] p-0 overflow-hidden flex flex-col">
        <DialogHeader className="bg-gradient-to-br from-heritage-blue to-future-blue text-white p-4 md:p-6 flex-shrink-0">
          <DialogTitle className="text-xl md:text-2xl font-bold flex items-center gap-2 text-white">
            <Users className="h-5 w-5 md:h-6 md:w-6" />
            {isRescheduling ? 'Reschedule Session' : 'Schedule Collaborative Session'}
          </DialogTitle>
          <DialogDescription className="text-blue-100">
            {employeeName}
          </DialogDescription>
        </DialogHeader>

        {/* Content */}
        <div className="p-4 md:p-6 space-y-4 md:space-y-6 overflow-y-auto flex-1 min-h-0">
          {/* Session Type Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="font-semibold text-blue-900 mb-1">{getSessionTypeLabel(sessionType)}</h3>
                <p className="text-sm text-blue-800">
                  People Leader-initiated objective collaboration is mandatory before objective creation.
                  Both parties should prepare relevant materials before the meeting.
                </p>
              </div>
            </div>
          </div>

          {/* Date Selection */}
          <div className="space-y-2">
            <Label className="text-base font-semibold">
              Meeting Date <span className="text-red-500">*</span>
            </Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full justify-start text-left font-normal"
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {scheduledDate && !isNaN(scheduledDate.getTime()) ? format(scheduledDate, 'PPP') : 'Pick a date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={scheduledDate}
                  onSelect={setScheduledDate}
                  disabled={(date: Date) => {
                    const today = new Date()
                    today.setHours(0, 0, 0, 0)
                    return date < today
                  }}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Time Selection */}
          <div className="space-y-2">
            <Label htmlFor="time" className="text-base font-semibold">
              Meeting Time <span className="text-red-500">*</span>
            </Label>
            <Select value={scheduledTime} onValueChange={setScheduledTime}>
              <SelectTrigger id="time">
                <SelectValue placeholder="Select time..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="08:00">08:00 AM</SelectItem>
                <SelectItem value="08:30">08:30 AM</SelectItem>
                <SelectItem value="09:00">09:00 AM</SelectItem>
                <SelectItem value="09:30">09:30 AM</SelectItem>
                <SelectItem value="10:00">10:00 AM</SelectItem>
                <SelectItem value="10:30">10:30 AM</SelectItem>
                <SelectItem value="11:00">11:00 AM</SelectItem>
                <SelectItem value="11:30">11:30 AM</SelectItem>
                <SelectItem value="12:00">12:00 PM</SelectItem>
                <SelectItem value="12:30">12:30 PM</SelectItem>
                <SelectItem value="13:00">01:00 PM</SelectItem>
                <SelectItem value="13:30">01:30 PM</SelectItem>
                <SelectItem value="14:00">02:00 PM</SelectItem>
                <SelectItem value="14:30">02:30 PM</SelectItem>
                <SelectItem value="15:00">03:00 PM</SelectItem>
                <SelectItem value="15:30">03:30 PM</SelectItem>
                <SelectItem value="16:00">04:00 PM</SelectItem>
                <SelectItem value="16:30">04:30 PM</SelectItem>
                <SelectItem value="17:00">05:00 PM</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Duration Selection */}
          <div className="space-y-2">
            <Label htmlFor="duration" className="text-base font-semibold">
              Duration
            </Label>
            <Select value={durationMinutes.toString()} onValueChange={(val) => setDurationMinutes(parseInt(val))}>
              <SelectTrigger id="duration">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="30">30 minutes</SelectItem>
                <SelectItem value="45">45 minutes</SelectItem>
                <SelectItem value="60">1 hour</SelectItem>
                <SelectItem value="90">1 hour 30 minutes</SelectItem>
                <SelectItem value="120">2 hours</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Availability Status */}
          {(scheduledDate && scheduledTime) && (
            <div className="p-4 rounded-lg border bg-gray-50">
              <div className="flex items-start gap-3">
                {isCheckingAvailability ? (
                  <>
                    <Loader2 className="h-5 w-5 text-blue-600 animate-spin flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-gray-900">Checking availability...</p>
                      <p className="text-sm text-gray-600">Verifying calendars for conflicts</p>
                    </div>
                  </>
                ) : availabilityStatus === 'available' ? (
                  <>
                    <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-green-900">Both calendars are free</p>
                      <p className="text-sm text-green-700">No conflicts detected for this time slot</p>
                    </div>
                  </>
                ) : availabilityStatus === 'conflict' ? (
                  <>
                    <UserX className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="font-medium text-amber-900">Scheduling conflict detected</p>
                      <p className="text-sm text-amber-700 mb-2">One or more attendees have conflicting appointments</p>
                      {conflicts.length > 0 && (
                        <div className="space-y-2 mt-3">
                          {conflicts.map((conflict, idx) => {
                            const affectsBoth = conflict.employee_ids && 
                              conflict.employee_ids.includes(user?.employeeId || '') && 
                              conflict.employee_ids.includes(employeeId)
                            
                            if (affectsBoth) {
                              return (
                                <div key={idx} className="bg-white p-3 rounded border border-amber-200">
                                  <p className="text-sm font-medium text-gray-900 mb-1">
                                    Both attendees (shared calendar)
                                  </p>
                                  {conflict.busy_items && conflict.busy_items.length > 0 && (
                                    <ul className="space-y-1">
                                      {conflict.busy_items.slice(0, 2).map((item: any, itemIdx: number) => (
                                        <li key={itemIdx} className="text-xs text-gray-600 flex items-start gap-1">
                                          <span className="text-amber-600">•</span>
                                          <span>{item.subject || 'Busy'}</span>
                                        </li>
                                      ))}
                                      {conflict.busy_items.length > 2 && (
                                        <li className="text-xs text-gray-500 italic">
                                          +{conflict.busy_items.length - 2} more
                                        </li>
                                      )}
                                    </ul>
                                  )}
                                </div>
                              )
                            }
                            
                            const isManager = conflict.employee_id === user?.employeeId
                            const displayName = isManager ? 'People Leader (You)' : `Team Member (${employeeName})`
                            
                            return (
                              <div key={idx} className="bg-white p-3 rounded border border-amber-200">
                                <p className="text-sm font-medium text-gray-900 mb-1">
                                  {displayName}
                                </p>
                                {conflict.busy_items && conflict.busy_items.length > 0 && (
                                  <ul className="space-y-1">
                                    {conflict.busy_items.slice(0, 2).map((item: any, itemIdx: number) => (
                                      <li key={itemIdx} className="text-xs text-gray-600 flex items-start gap-1">
                                        <span className="text-amber-600">•</span>
                                        <span>{item.subject || 'Busy'}</span>
                                      </li>
                                    ))}
                                    {conflict.busy_items.length > 2 && (
                                      <li className="text-xs text-gray-500 italic">
                                        +{conflict.busy_items.length - 2} more
                                      </li>
                                    )}
                                  </ul>
                                )}
                              </div>
                            )
                          })}
                        </div>
                      )}
                      <p className="text-xs text-amber-600 mt-2">
                        You can still schedule, but attendees may need to reschedule existing meetings
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    <AlertCircle className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-gray-700">Availability check unavailable</p>
                      <p className="text-sm text-gray-600">Calendar service may be offline</p>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Meeting Type */}
          <div className="space-y-2">
            <Label htmlFor="meetingType" className="text-base font-semibold">
              Meeting Type
            </Label>
            <Select value={meetingType} onValueChange={(val) => setMeetingType(val as any)}>
              <SelectTrigger id="meetingType">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="virtual">
                  <div className="flex items-center gap-2">
                    <Video className="h-4 w-4" />
                    Virtual (MS Teams)
                  </div>
                </SelectItem>
                <SelectItem value="face-to-face">
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    Face-to-Face
                  </div>
                </SelectItem>
                <SelectItem value="hybrid">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Hybrid
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Location (for non-virtual) */}
          {meetingType !== 'virtual' && (
            <div className="space-y-2">
              <Label htmlFor="location" className="text-base font-semibold">
                Location <span className="text-red-500">*</span>
              </Label>
              <Select value={location} onValueChange={setLocation}>
                <SelectTrigger id="location">
                  <SelectValue placeholder="Select location..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="manager-office">People Leader's Office</SelectItem>
                  <SelectItem value="meeting-room">Meeting Room</SelectItem>
                  <SelectItem value="conference-room">Conference Room</SelectItem>
                  <SelectItem value="other">Other Location</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Agenda */}
          <div className="space-y-2">
            <Label htmlFor="agenda" className="text-base font-semibold">
              Meeting Agenda
            </Label>
            <Textarea
              id="agenda"
              placeholder="Add specific discussion points or preparation requirements..."
              rows={3}
              value={agenda}
              onChange={(e) => setAgenda(e.target.value)}
            />
          </div>

          {/* Default Agenda Items */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h4 className="font-semibold text-sm text-gray-900 mb-2 flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Standard Agenda Items
            </h4>
            <ul className="text-xs text-gray-700 space-y-1">
              <li>• Confirm F&O role profile accuracy</li>
              <li>• Review strategy cascade and organisational context</li>
              <li>• Discuss objective sources (prior year, templates, library)</li>
              <li>• Collaboratively set SMART objectives</li>
              <li>• Confirm objective approval and alignment</li>
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t bg-gray-50 p-4 flex justify-end gap-2 flex-shrink-0">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleSchedule}
            className="bg-heritage-blue hover:bg-heritage-blue/90"
            disabled={!scheduledDate || !scheduledTime}
          >
            <CalendarIcon className="h-4 w-4 mr-2" />
            {isRescheduling ? 'Reschedule Session' : 'Schedule Session'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
